// src/lib/caution-link-hint.js
// Texte d'aide affiché sous le lien de caution dans la modale résa (onglet
// Paiements). Helper PUR — aucun effet de bord, aucune décision Stripe.
//
// ⚠️ TERRAIN DE LITIGE. Ce texte est lu par l'admin puis répété au voyageur
// (« on ne vous débite pas, c'est une empreinte »). L'ancien texte disait
// « pré-autorisation 30 jours sur sa CB » — deux erreurs :
//   1. Les 30 jours sont la durée de validité du LIEN FBZ (form_tokens.expires_at
//      = end_date + 30 j, cf. api/stripe.js), pas la durée du blocage bancaire.
//      Le hold Stripe tient ~7 jours : l'Extended Authorization 30 j a été
//      RETIRÉE (hotfix C34, sous-comptes Connect non éligibles).
//   2. Au-delà de CAUTION_HOLD_MAX_NIGHTS nuits (4 par défaut), api/stripe.js
//      passe `capture_method: 'automatic'` — ce n'est plus une pré-autorisation
//      mais un DÉBIT RÉEL, remboursé après le séjour.
//
// La décision empreinte/débit n'est PAS ré-implémentée ici : elle est lue via
// `cautionModeForNights` (jumeau ESM de lib/caution-mode.js, ADR-174), le MÊME
// module que celui dont dérive la décision Stripe. Le texte ne peut donc pas
// diverger de ce que le code fait.

// P-2.56 (30/10) : mode et seuil par LA porte cautionPourSejour (caution figée
// de la résa si l'écran la donne, sinon seuil du logement, sinon seuil commun) —
// la même que la session Stripe. Sans `opts`, la règle commune d'avant.
import { cautionPourSejour } from "./caution-figee"

/**
 * Texte d'aide du lien de caution, exact pour CETTE réservation.
 *
 * @param {number|null|undefined} nbNuits — nuits du séjour (end − start). Une
 *   valeur absente / non finie signifie « dates incomplètes » : api/stripe.js
 *   retombe alors sur le mode le plus prudent (empreinte), et le texte le dit.
 * @param {{ figee?: object|null, log?: object|null,
 *           serveur?: { mode: 'empreinte'|'debit', seuil: number } | null }} [opts]
 *   — `serveur` = ce que le serveur appliquera à CE lien (token_resolve : caution
 *   figée, y compris sur la demande d'un direct) ; il prime. Sinon la résa
 *   (colonnes caution_*) et le logement aplati.
 * @returns {{ mode: 'empreinte'|'debit', nightsKnown: boolean, text: string }}
 */
export function cautionLinkHint(nbNuits, opts) {
  const nightsKnown = Number.isFinite(nbNuits) && nbNuits > 0
  const caution = cautionPourSejour({
    figee: (opts && opts.figee) || null,
    log: (opts && opts.log) || null,
    nuits: Number.isInteger(nbNuits) ? nbNuits : 0,
  })
  const serveur = opts && opts.serveur
  const serveurLisible =
    serveur &&
    (serveur.mode === "empreinte" || serveur.mode === "debit") &&
    Number.isInteger(serveur.seuil) &&
    serveur.seuil >= 1
  const mode = serveurLisible ? serveur.mode : caution.mode
  const seuil = serveurLisible ? serveur.seuil : caution.seuil

  // Rappel commun : la date affichée au-dessus du texte est celle du token FBZ.
  const lienNote =
    "La date ci-dessus est la validité du lien FBZ, pas la durée du blocage bancaire."

  if (mode === "debit") {
    return {
      mode,
      nightsKnown,
      text:
        `Page FBZ brandée. Séjour de ${nbNuits} nuits (plus de ${seuil}) : ` +
        "la caution est réellement débitée sur la carte du voyageur, puis remboursée " +
        "après le séjour — aucune empreinte bancaire ne couvre un séjour aussi long. " +
        lienNote
    }
  }

  if (!nightsKnown) {
    return {
      mode,
      nightsKnown,
      text:
        "Page FBZ brandée. Dates de séjour incomplètes : la caution sera prise en " +
        "empreinte bancaire (mode par défaut le plus prudent, identique côté Stripe) — " +
        "somme bloquée sur la carte pendant environ 7 jours : rien n'est prélevé " +
        "sauf dégât constaté (capture), sinon libérée pendant ce délai. " +
        lienNote
    }
  }

  return {
    mode,
    nightsKnown,
    text:
      `Page FBZ brandée. Séjour de ${nbNuits} nuit${nbNuits > 1 ? "s" : ""} ` +
      `(${seuil} au plus) : empreinte bancaire — la somme est bloquée ` +
      "sur la carte du voyageur pendant environ 7 jours : rien n'est prélevé sauf " +
      "dégât constaté (capture), sinon elle est libérée pendant ce délai. " +
      lienNote
  }
}
